// Helper murni untuk extract daftar media dari manifest (untuk prefetcher).

import type { Manifest } from "./renderer.js";

interface MediaRef {
  id: string;
  url: string;
  mime_type: string | null;
  name: string;
}

/**
 * Cek apakah media adalah URL embed (YouTube/Vimeo/web page generic) yang
 * tidak boleh di-prefetch sebagai blob — selalu load via iframe langsung.
 *
 * Catatan: backend addFromUrl set type='video' untuk YouTube/Vimeo/direct
 * supaya masuk kategori Videos di UI; cek mime_type / pattern URL untuk
 * detect embed (vs file video murni yang harus di-cache).
 */
function isEmbedMedia(m: {
  media_type: string;
  url: string;
  mime_type: string | null;
}): boolean {
  if (m.media_type === "url") return true;
  if (m.mime_type === "application/x-youtube") return true;
  if (m.mime_type === "application/x-vimeo") return true;
  if (m.mime_type === "application/x-generic_iframe") return true;
  if (/youtube\.com\/embed\/|youtu\.be\//i.test(m.url)) return true;
  if (/player\.vimeo\.com\/video\//i.test(m.url)) return true;
  return false;
}

/**
 * Extract media list yang BISA & PERLU di-prefetch (image, video file).
 * Skip:
 *   - Type 'url' generic (web page iframe)
 *   - Media type='video' tapi URL embed YouTube/Vimeo (tidak bisa di-blob)
 *   - Streaming live (HLS/RTMP) tidak cocok cache
 */
export function extractMediaList(manifest: Manifest): MediaRef[] {
  if (manifest.type === "media") {
    if (isEmbedMedia(manifest.media)) return [];
    return [
      {
        id: manifest.media.id,
        url: manifest.media.url,
        mime_type: manifest.media.mime_type,
        name: manifest.media.name,
      },
    ];
  }
  if (manifest.type === "playlist") {
    return manifest.playlist.items
      .filter((it) => it.media !== null && !isEmbedMedia(it.media))
      .map((it) => ({
        id: it.media!.id,
        url: it.media!.url,
        mime_type: it.media!.mime_type,
        name: it.media!.name,
      }));
  }
  // layout: gabungkan semua media dari semua zone (media + playlist items).
  const list: MediaRef[] = [];
  for (const z of manifest.layout.zones) {
    if (!z.content) continue;
    if (z.content.kind === "media" && !isEmbedMedia(z.content.media)) {
      list.push({
        id: z.content.media.id,
        url: z.content.media.url,
        mime_type: z.content.media.mime_type,
        name: z.content.media.name,
      });
    } else if (z.content.kind === "playlist") {
      for (const it of z.content.playlist.items) {
        if (it.media && !isEmbedMedia(it.media)) {
          list.push({
            id: it.media.id,
            url: it.media.url,
            mime_type: it.media.mime_type,
            name: it.media.name,
          });
        }
      }
    }
  }
  return list;
}

// Rewrite localhost di URL ke hostname saat ini (untuk LAN access).
export function rewriteUrlForLan(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
      u.hostname = window.location.hostname;
    }
    return u.toString();
  } catch {
    return url;
  }
}

// Apply LAN rewrite ke semua URL di media list.
export function withLanRewrite(items: MediaRef[]): MediaRef[] {
  return items.map((it) => ({ ...it, url: rewriteUrlForLan(it.url) }));
}
